import React, { useCallback } from "react";

import Form from "antd/es/form";
import { useForm, useWatch } from "antd/es/form/Form";
import { defineMessages, useIntl } from "react-intl";

import { FormItem } from "@mapmycustomers/ui";

import FormFields from "@app/component/FormFields";
import ModeSwitch from "@app/component/leadFinder/components/AddRecordsWizard/components/ModeSwitch";
import Routes from "@app/component/leadFinder/components/AddRecordsWizard/components/Routes";
import useStepTitle from "@app/component/leadFinder/components/AddRecordsWizard/useStepTitle";
import { IWizardStep, StandardStep } from "@app/component/wizard";

import AddRecordsWizardState from "../../AddRecordsWizardState";

import styles from "./SelectTypeStep.module.scss";

const messages = defineMessages({
  routes: {
    id: "leadFinder.addRecordsWizard.selectTypeStep.routesLabel",
    defaultMessage: "Add leads to the following route(s)",
    description: "Add leads to the following route(s)",
  },
  subtitle: {
    id: "leadFinder.addRecordsWizard.selectTypeStep.subtitle",
    defaultMessage: "Import as",
    description: "Lead Finder - Import as title, above the record type switch",
  },
});

interface SelectTypeStepProps extends IWizardStep<AddRecordsWizardState> {}

type FormFields = Pick<AddRecordsWizardState, "entityType" | "routeIdsOrRoutePayloads">;

const SelectTypeStep = ({
  onCancel,
  onGoForward,
  onSetState,
  state,
  ...props
}: SelectTypeStepProps) => {
  const intl = useIntl();
  const [form] = useForm<FormFields>();

  const title = useStepTitle(state.selectedLeads.length);

  const formInitialValues: FormFields = {
    entityType: state.entityType,
    routeIdsOrRoutePayloads: state.routeIdsOrRoutePayloads,
  };

  const handleFinish = useCallback(
    (formValues: FormFields) => {
      onSetState((prevState) => ({ ...prevState, ...formValues }));
      onGoForward();
    },
    [onGoForward, onSetState]
  );

  const entityType = useWatch("entityType", form);

  return (
    <StandardStep
      backButtonHidden
      onCancel={onCancel}
      onGoForward={form.submit}
      title={title}
      {...props}
    >
      <div className={styles.content}>
        <div className={styles.subtitle}>{intl.formatMessage(messages.subtitle)}</div>
        <Form
          form={form}
          initialValues={formInitialValues}
          layout="vertical"
          onFinish={handleFinish}
        >
          <FormItem name="entityType">
            <ModeSwitch allowedEntityTypes={state.allowedEntityTypes} />
          </FormItem>
          {state.includeRoutesStep && (
            <FormItem label={intl.formatMessage(messages.routes)} name="routeIdsOrRoutePayloads">
              <Routes entityType={entityType} />
            </FormItem>
          )}
        </Form>
      </div>
    </StandardStep>
  );
};

export default SelectTypeStep;
